// Course Services
// Firestore course CRUD operations using ServiceBase

import {
    collection,
    query,
    where,
    orderBy,
    limit,
    getDocs,
    addDoc
} from 'firebase/firestore';
import { db } from '@lib/firebase';

import { CourseError, ValidationError } from '@/shared/api/errors/ApiError';
import { getTimestamps, getUpdatedTimestamp } from '@/shared/utils/api/timestampHelper';
import ServiceBase from '@/shared/api/base/ServiceBase';

const COURSES_COLLECTION = 'courses';
const CACHE_TTL = 15 * 60 * 1000; // 15 minutes for courses

export interface Course {
    id: string;
    title: string;
    description: string;
    category: string;
    featured?: boolean;
    moduleOrder?: string[];
    enrolledStudents?: number;
    averageRating?: number;
    totalReviews?: number;
    completionRate?: number;
    createdAt?: any;
    updatedAt?: any;
    [key: string]: any;
}

export interface CourseStats {
    totalStudents: number;
    averageRating: number;
    totalReviews: number;
    completionRate: number;
}

class CourseService extends ServiceBase {
    constructor() {
        super('CourseService');
    }

    /**
     * Get all courses
     * @returns {Promise<Course[]>} List of courses
     */
    async getCourses(): Promise<Course[]> {
        try {
            // E2E Test Mock Injection
            if (typeof window !== 'undefined' && (window as any).MOCK_COURSES) {
                this.log('Returning MOCKED Courses for E2E testing');
                return (window as any).MOCK_COURSES;
            }

            const cacheKey = 'all_courses';
            const cachedCourses = this.cache.get<Course[]>(cacheKey);
            if (cachedCourses) {
                return cachedCourses;
            }

            const coursesRef = collection(db, COURSES_COLLECTION);
            const q = query(coursesRef, orderBy('createdAt', 'desc'));
            const querySnapshot = await getDocs(q);

            const courses = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            })) as Course[];

            this.cache.set(cacheKey, courses, CACHE_TTL);
            return courses;
        } catch (error) {
            this.logError(error, { method: 'getCourses' });
            throw error;
        }
    }

    /**
     * Get course by ID
     * @param {string} courseId Course ID
     * @returns {Promise<Course>} Course data
     */
    async getCourseById(courseId: string): Promise<Course> {
        try {
            this.validate.validateCourseId(courseId);

            // E2E Test Mock Injection
            if (typeof window !== 'undefined' && (window as any).MOCK_COURSES) {
                const mockCourse = ((window as any).MOCK_COURSES as Course[]).find(c => c.id === courseId);
                if (mockCourse) {
                    this.log('Returning MOCKED Course by ID for E2E testing');
                    return mockCourse;
                }
            }

            const cacheKey = `course_${courseId}`;
            const cachedCourse = this.cache.get<Course>(cacheKey);
            if (cachedCourse) {
                return cachedCourse;
            }

            const courseData = await this.getDoc(COURSES_COLLECTION, courseId) as Course;

            this.cache.set(cacheKey, courseData, CACHE_TTL);
            return courseData;
        } catch (error: any) {
            if (error.code === 'NOT_FOUND') {
                throw new CourseError('Course not found', courseId);
            }
            this.logError(error, { method: 'getCourseById', courseId });
            throw error;
        }
    }

    /**
     * Get featured courses
     * @param {number} limitCount Maximum number of courses to return
     * @returns {Promise<Course[]>} List of featured courses
     */
    async getFeaturedCourses(limitCount = 6): Promise<Course[]> {
        try {
            if (typeof limitCount !== 'number' || limitCount <= 0) {
                throw new ValidationError('limitCount must be a positive number');
            }

            const coursesRef = collection(db, COURSES_COLLECTION);
            const q = query(
                coursesRef,
                where('featured', '==', true),
                orderBy('createdAt', 'desc'),
                limit(limitCount)
            );
            const querySnapshot = await getDocs(q);

            return querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            })) as Course[];
        } catch (error) {
            this.logError(error, { method: 'getFeaturedCourses', limitCount });
            throw error;
        }
    }

    /**
     * Get courses by category
     * @param {string} category Category name
     * @returns {Promise<Course[]>} List of courses in category
     */
    async getCoursesByCategory(category: string): Promise<Course[]> {
        try {
            if (!category || typeof category !== 'string') {
                throw new ValidationError('category must be a non-empty string');
            }

            const coursesRef = collection(db, COURSES_COLLECTION);
            const q = query(
                coursesRef,
                where('category', '==', category),
                orderBy('createdAt', 'desc')
            );
            const querySnapshot = await getDocs(q);

            return querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            })) as Course[];
        } catch (error) {
            this.logError(error, { method: 'getCoursesByCategory', category });
            throw error;
        }
    }

    /**
     * Create new course
     * @param {Partial<Course>} courseData Course data
     * @returns {Promise<Course>} Created course
     */
    async createCourse(courseData: Partial<Course>): Promise<Course> {
        try {
            if (!courseData || typeof courseData !== 'object') {
                throw new ValidationError('courseData must be a non-empty object');
            }
            if (!courseData.title || typeof courseData.title !== 'string') {
                throw new ValidationError('courseData.title must be a non-empty string');
            }
            if (!courseData.description || typeof courseData.description !== 'string') {
                throw new ValidationError('courseData.description must be a non-empty string');
            }

            const coursesRef = collection(db, COURSES_COLLECTION);
            const newCourse = {
                ...courseData,
                ...getTimestamps()
            };

            const docRef = await addDoc(coursesRef, newCourse);
            this.cache.invalidate('all_courses');

            return {
                id: docRef.id,
                ...newCourse
            } as Course;
        } catch (error) {
            this.logError(error, { method: 'createCourse' });
            throw error;
        }
    }

    /**
     * Update course
     * @param {string} courseId Course ID
     * @param {Partial<Course>} updates Updates to apply
     * @returns {Promise<Course>} Updated course
     */
    async updateCourse(courseId: string, updates: Partial<Course>): Promise<Course> {
        try {
            this.validate.validateCourseId(courseId);
            if (!updates || typeof updates !== 'object') {
                throw new ValidationError('updates must be a non-empty object');
            }

            const updateData = {
                ...updates,
                ...getUpdatedTimestamp()
            };

            await this.updateDoc(COURSES_COLLECTION, courseId, updateData);
            this.cache.invalidate(`course_${courseId}`);
            this.cache.invalidate('all_courses');

            return await this.getCourseById(courseId);
        } catch (error) {
            this.logError(error, { method: 'updateCourse', courseId });
            throw error;
        }
    }

    /**
     * Delete course
     * @param {string} courseId Course ID
     * @returns {Promise<void>}
     */
    async deleteCourse(courseId: string): Promise<void> {
        try {
            this.validate.validateCourseId(courseId);

            await this.deleteDoc(COURSES_COLLECTION, courseId);
            this.cache.invalidate(`course_${courseId}`);
            this.cache.invalidate('all_courses');
        } catch (error) {
            this.logError(error, { method: 'deleteCourse', courseId });
            throw error;
        }
    }

    /**
     * Search courses
     * @param {string} searchTerm Search term
     * @returns {Promise<Course[]>} List of matching courses
     */
    async searchCourses(searchTerm: string): Promise<Course[]> {
        try {
            if (!searchTerm || typeof searchTerm !== 'string') {
                throw new ValidationError('searchTerm must be a non-empty string');
            }

            const coursesRef = collection(db, COURSES_COLLECTION);
            const querySnapshot = await getDocs(coursesRef);

            const searchTermLower = searchTerm.toLowerCase();

            return querySnapshot.docs
                .map(doc => ({ id: doc.id, ...doc.data() } as Course))
                .filter(courseData => {
                    const searchString = `${courseData.title} ${courseData.description} ${courseData.category}`.toLowerCase();
                    return searchString.includes(searchTermLower);
                });
        } catch (error) {
            this.logError(error, { method: 'searchCourses', searchTerm });
            throw error;
        }
    }

    /**
     * Get course statistics
     * @param {string} courseId Course ID
     * @returns {Promise<CourseStats>} Course statistics
     */
    async getCourseStats(courseId: string): Promise<CourseStats> {
        try {
            this.validate.validateCourseId(courseId);

            const courseData = await this.getCourseById(courseId);

            return {
                totalStudents: courseData.enrolledStudents || 0,
                averageRating: courseData.averageRating || 0,
                totalReviews: courseData.totalReviews || 0,
                completionRate: courseData.completionRate || 0
            };
        } catch (error) {
            this.logError(error, { method: 'getCourseStats', courseId });
            throw error;
        }
    }
}

const courseServices = new CourseService();

// Maintain backward compatibility for named exports
export const getCourses = () => courseServices.getCourses();
export const getCourseById = (id: string) => courseServices.getCourseById(id);
export const getFeaturedCourses = (limitNum: number) => courseServices.getFeaturedCourses(limitNum);
export const getCoursesByCategory = (cat: string) => courseServices.getCoursesByCategory(cat);
export const createCourse = (data: Partial<Course>) => courseServices.createCourse(data);
export const updateCourse = (id: string, updates: Partial<Course>) => courseServices.updateCourse(id, updates);
export const deleteCourse = (id: string) => courseServices.deleteCourse(id);
export const searchCourses = (term: string) => courseServices.searchCourses(term);
export const getCourseStats = (id: string) => courseServices.getCourseStats(id);

export default courseServices;
